import React, { useState, useEffect, useContext, useRef } from 'react';
import { Navigate } from "react-router-dom";
import './NotesHistory.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NotesHistoryRow from './NotesHistoryRow';
import convenienceApi from '../../common/api/convenience';
import Filter from '../Filter/Filter';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import MonitoredIndividualSummaryBar from '../MonitoredIndividualSummaryBar/MonitoredIndividualSummaryBar';
import UnitSummaryBar from '../UnitSummaryBar/UnitSummaryBar';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';

const NOTES_PER_LOAD = 5;

export default function NotesHistory() {
  const environmentContext = useContext(EnvironmentContext);
  const { activeUser: user, selectedMonitoredIndividual, selectedUnit, hasToken } = environmentContext;
  const [inputSearchTerms, setInputSearchTerms] = useState('');
  const [trueSearchTerms, setTrueSearchTerms] = useState('');
  const [sortDirection, setSortDirection] = useState('descending');
  const [noteType, setNoteType] = useState(environmentContext.activeUser.groupTypeString === 'Family' ? 'family' : 'all');
  const [selectedNote, setSelectedNote] = useState(null);
  const [userSelectedNote, setUserSelectedNote] = useState(null);
  const [notesOnDeck, setNotesOnDeck] = useState(null);
  const [notes, setNotes] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(false);
  const [searchTermUpdateTimeout, setSearchTermUpdateTimeout] = useState(null);
  const [needsFloatCurrentNote, setNeedsFloatCurrentNote] = useState(false);
  const inputSearchTermsRef = useRef('');
  const currentNoteRef = useRef(null);
  const currentNoteBodyRef = useRef(null);
  const notesHistoryRef = useRef(null);
  const isLoading = useRef(false);
  const selectedNoteRef = useRef(null);

  if (logComponent('NotesHistory')) { console.debug('NotesHistory rendered'); }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('NotesHistory')) { console.debug('NotesHistory onMount'); }
   window.addEventListener('scroll', floatCurrentNote);
    window.addEventListener('resize', floatCurrentNote);
    if (!hasToken) return;
    window.scrollTo(0, 0);

    loadNotes();
    return function cleanup() {
      window.removeEventListener('scroll', floatCurrentNote);
      window.removeEventListener('resize', floatCurrentNote);
    };
  }, []);


  useEffect(() => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;
    loadNotes();
  }, [environmentContext.hasToken])

  useEffect(() => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory useEffect [needsFloatCurrentNote]'); }
    if (needsFloatCurrentNote === true) {
      floatCurrentNote();
      setNeedsFloatCurrentNote(false);
    }
  }, [needsFloatCurrentNote])


  useEffect(() => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory useEffect [environmentContext.selectedMonitoredIndividual, trueSearchTerms, sortDirection, noteType]'); }
    if (!hasToken) return;
    loadNotes();
  }, [environmentContext.selectedMonitoredIndividual, trueSearchTerms, sortDirection, noteType])

  const setInputSearchTermsRef = (el) => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory setInputSearchTermsRef'); }
    inputSearchTermsRef.current = el;
  }

  const setCurrentNoteRef = (el) => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory setCurrentNoteRef'); }
    currentNoteRef.current = el;
  }

  const setCurrentNoteBodyRef = (el) => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory setCurrentNoteBodyRef'); }
    currentNoteBodyRef.current = el;
  }

  const setNotesHistoryRef = (el) => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory setNotesHistoryRef'); }
    notesHistoryRef.current = el;
  }

  const setSelectedNoteRef = (el) => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory setSelectedNoteRef'); }
    selectedNoteRef.current = el;
    setSelectedNote(el);
  }

  const loadNotes = () => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory loadNotes'); }

    if (!hasToken || isLoading.current) return;
    isLoading.current = true;
    setSelectedNote(null);
    setUserSelectedNote(null);
    setNotes(null);
    setNotesOnDeck(null);
    setNotesLoading(true);
    setNotesError(false);

    convenienceApi.getNotesHistory(noteType, selectedMonitoredIndividual, selectedUnit, 0, NOTES_PER_LOAD * 2, sortDirection, trueSearchTerms)
      .then(
        result => {
          setNotes(result.filter((note, index) => index < NOTES_PER_LOAD));
          setNotesOnDeck(result.filter((note, index) => index >= NOTES_PER_LOAD));
          setSelectedNote(result.length > 0 ? result[0] : null);
          setNotesLoading(false);
          setNotesError(false);
        },
        error => {
          setNotes(null);
          setNotesOnDeck(null);
          setNotesLoading(false);
          setNotesError(true);
          });
    isLoading.current = false;
  }

  const loadMoreNotes = () => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory loadMoreNotes'); }

    if (!hasToken) return;

    const moreNotesToLoad = notesOnDeck.length >= NOTES_PER_LOAD;
    const totalNotesLoaded = notes.length + notesOnDeck.length;

    // Add notes on deck to notes
    setNotes(notes.concat(notesOnDeck));
    setNotesOnDeck(null);

    // If there are no more notes to load (i.e., if notesOnDeck was < 10), bail
    if (!moreNotesToLoad) return;

    // Make call to fetch more notes
    convenienceApi.getNotesHistory(noteType, selectedMonitoredIndividual, selectedUnit, totalNotesLoaded, NOTES_PER_LOAD, sortDirection, trueSearchTerms)
      .then(
        result => {
          setNotesOnDeck(result);
        },
        error => {
          // TODO: Better error handling in this case 
          setNotesOnDeck([]);
        });
  }

  const updateSearchTerms = (e) => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory updateSearchTerms'); }
    if (searchTermUpdateTimeout) clearTimeout(searchTermUpdateTimeout);

    setInputSearchTermsRef(e.target.value);
    setSearchTermUpdateTimeout(setTimeout(updateTrueSearchTerms, 350));
    
  }

  const updateTrueSearchTerms = () => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory updateTrueSearchTerms'); }
    setTrueSearchTerms(inputSearchTermsRef.current);
    setSearchTermUpdateTimeout(null);
  }

  const updateNoteType = (noteTypeFilter) => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory updateNoteType'); }
    const values = noteTypeFilter.criteria.map(criterion => criterion.acceptedValues[0]);

    let newNoteType;

    if (values.includes('Family') && values.includes('Staff')) {
      newNoteType = 'all';
    } else if (values.includes('Family')) {
      newNoteType = 'family';
    } else if (values.includes('Staff')) {
      newNoteType = 'staff';
    } else {
      newNoteType = 'all';
    }

    if (newNoteType) setNoteType(newNoteType);
  }

  const setSelectedNoteThis = (note) => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory setSelectedNoteThis'); }
    setSelectedNoteRef(note);
    setUserSelectedNote(note);
    setNeedsFloatCurrentNote(true);


    if (window.matchMedia('(max-width: 600px)').matches) window.scroll(0,0);
  }

  const hideSelectedNote = () => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory hideSelectedNote'); }
    setUserSelectedNote(null);
  }

  const toggleSortDirection = () => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory toggleSortDirection'); }
    const newDirection = sortDirection === 'ascending' ? 'descending' : 'ascending';
    setSortDirection(newDirection)
  }

  const floatCurrentNote = () => {
    if (logComponent('NotesHistory')) { console.debug('NotesHistory floatCurrentNote'); }
    if (!currentNoteRef.current || !currentNoteBodyRef.current || !notesHistoryRef.current) return;

    const currentNoteRect = currentNoteRef.current.getBoundingClientRect();
    const currentNoteBodyRect = currentNoteBodyRef.current.getBoundingClientRect();
    const notesHistoryRect = notesHistoryRef.current.getBoundingClientRect();

    let distanceFromNoteToHeader = currentNoteRect.top - notesHistoryRect.top + 32;

    let newOffset = Math.max(0, window.scrollY - distanceFromNoteToHeader);

    const floatMax = currentNoteRect.height - currentNoteBodyRect.height - 64; // TODO: Calculate this value

    newOffset = Math.min(newOffset, floatMax);
    newOffset = Math.max(newOffset, 0);

    currentNoteBodyRef.current.style.top = newOffset + 'px';

  }

  if (user.groupTypeString === 'Facility' && (!selectedMonitoredIndividual && !selectedUnit)) return (<Navigate to="/" />);

  return (
    <div className="Notes-History" ref={setNotesHistoryRef}>
      {
        user.groupTypeString === 'Facility' &&
        <div className="Notes-History__summary-bar">
          {
            selectedMonitoredIndividual &&
            <MonitoredIndividualSummaryBar />
          }
          {
            selectedUnit &&
            <UnitSummaryBar />
          }
        </div>
      }

      <div className="Notes-History__main">
        <p className="Notes-History__heading">Notes history</p>

        <div className={'Notes-History__filter-row' + (userSelectedNote ? ' Notes-History__filter-row--hidden-sm' : '')}>
          <div className="Notes-History__search-input-wrapper">
            <FontAwesomeIcon icon="search" className="Notes-History__search-icon" />
            <input
              type="text"
              className="Notes-History__search-input"
              placeholder="Search notes"
              value={inputSearchTermsRef.current}
              onChange={updateSearchTerms} />
          </div>

          {
            user.groupTypeString === 'Facility' &&
            <div className="Notes-History__filter-wrapper">
              <Filter
                filters={[
                  {
                    name: 'noteType',
                    options: [{
                      text: 'Internal',
                      criteria: [{
                        field: 'Type',
                        acceptedValues: ['Staff']
                      }]
                    }, {
                      text: 'Family',
                      criteria: [{
                        field: 'Type',
                        acceptedValues: ['Family']
                      }]
                    }],
                    title: 'Note type',
                    type: 'checkbox'
                  }
                ]}
                onChange={updateNoteType} />
            </div>
          }
        </div>

        <div className="Notes-History__notes">
          <div className="Notes-History__all-notes">
            <div className={'Notes-History__note-list-heading-row' + (user.groupTypeString === 'Facility' ? ' three-column' : ' two-column')}>
              <p className="Notes-History__note-list-heading">Staff member</p>
              <p
                className={'Notes-History__note-list-heading ' + sortDirection}
                onClick={toggleSortDirection}>
                <span className="Notes-History__note-list-heading-text">Date</span>
                <FontAwesomeIcon
                  icon={(sortDirection === 'ascending' ? 'arrow-up' : 'arrow-down')}
                  className="Notes-History__sort-icon" />
              </p>
              {
                user.groupTypeString === 'Facility' &&
                <p className="Notes-History__note-list-heading">Visibility</p>
              }
            </div>
            {
              notes &&
              (notes.length === 0 ?
                <p className="Notes-History__empty-message">No notes found.</p> :
                <ul className="Notes-History__note-list">
                  {
                    notes.map(note => {
                      return <NotesHistoryRow key={note.id} note={note} user={user} selected={note === selectedNote} onClick={setSelectedNoteThis} />;
                    })
                  }
                </ul>)
            }

            <ModuleLoadingStatus isError={notesError} isLoading={notesLoading} reloadAction={loadNotes} margin="2rem" />

            {
              notesOnDeck &&
              notesOnDeck.length > 0 &&
              <button
                className="Notes-History__load-more-button"
                onClick={loadMoreNotes}>Load more</button>
            }
          </div>
          <div className="Notes-History__current-note" ref={setCurrentNoteRef}>
            <p className="Notes-History__current-note-heading">Note details</p>
            {
              notes &&
              notes.length > 0 &&
              <p className="Notes-History__current-note-body" ref={setCurrentNoteBodyRef}>{selectedNote ? selectedNote.note : ''}</p>
            }

          </div>
        </div>

        <div className="Notes-History__notes-sm">
          {
            !userSelectedNote ?
              <div className="Notes-History__all-notes">
                <div className={'Notes-History__note-list-heading-row' + (user.groupTypeString === 'Facility' ? ' three-column' : ' two-column')}>
                  <p className="Notes-History__note-list-heading">Staff member</p>
                  <p
                    className={'Notes-History__note-list-heading ' + sortDirection}
                    onClick={toggleSortDirection}>Date
                    <FontAwesomeIcon
                      icon={(sortDirection === 'ascending' ? 'arrow-up' : 'arrow-down')}
                      className="Notes-History__sort-icon" />
                  </p>
                  {
                    user.groupTypeString === 'Facility' &&
                    <p className="Notes-History__note-list-heading">Visibility</p>
                  }
                </div>
                {
                  notes &&
                  (notes.length === 0 ?
                    <p className="Notes-History__empty-message">No notes found.</p> :
                    <ul className="Notes-History__note-list">
                      {
                        notes && notes.map(note => <NotesHistoryRow key={note.id} note={note} user={user} onClick={setSelectedNoteThis} />)
                      }
                    </ul>)
                }
               <ModuleLoadingStatus isError={notesError} isLoading={notesLoading} reloadAction={loadNotes} margin="2rem" />
                {
                  notesOnDeck &&
                  notesOnDeck.length > 0 &&
                  <button
                    className="Notes-History__load-more-button"
                    onClick={loadMoreNotes}>Load more</button>
                }
              </div> :
              <div className='Notes-History__current-note-sm'>
                <button
                  className="Notes-History__close-note-button"
                  onClick={hideSelectedNote}>
                  <FontAwesomeIcon icon="chevron-left" className="Notes-History__close-note-button-icon" /> Back
                </button>
                <div className={'Notes-History__note-list-heading-row' + (user.groupTypeString === 'Facility' ? ' three-column' : ' two-column')}>
                  <p className="Notes-History__note-list-heading">Staff member</p>
                  <p className="Notes-History__note-list-heading">Date</p>
                  {
                    user.groupTypeString === 'Facility' &&
                    <p className="Notes-History__note-list-heading">Visibility</p>
                  }
                </div>
                <ul className="Notes-History__current-note-detail">
                  <NotesHistoryRow note={userSelectedNote} user={user} selected={false} />
                </ul>
                <p className="Notes-History__current-note-heading-sm">Note details</p>
                <p className="Notes-History__current-note-body-sm">{userSelectedNote.note}</p>
              </div>
          }
        </div>
      </div>
    </div>
  );
}